import { date, index, jsonb, numeric, pgTable, text, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const contacts = pgTable(
  'contacts',
  {
    id: id(),
    userId: text('user_id').notNull(),
    fullName: varchar('full_name', { length: 200 }).notNull(),
    company: varchar('company', { length: 200 }),
    role: varchar('role', { length: 200 }),
    email: varchar('email', { length: 320 }),
    linkedinUrl: text('linkedin_url'),
    source: varchar('source', { length: 32 }),
    confidence: numeric('confidence', { precision: 4, scale: 3 }),
    notes: text('notes'),
    metadata: jsonb('metadata'),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('contacts_user_idx').on(t.userId),
    companyIdx: index('contacts_company_idx').on(t.company),
  }),
);

export const outreachDrafts = pgTable(
  'outreach_drafts',
  {
    id: id(),
    userId: text('user_id').notNull(),
    contactId: text('contact_id'),
    jobId: text('job_id'),
    subject: varchar('subject', { length: 200 }).notNull(),
    body: text('body').notNull(),
    threadId: varchar('thread_id', { length: 128 }),
    scheduledFor: date('scheduled_for'),
    sentAt: date('sent_at'),
    status: varchar('status', { length: 24 }).default('draft').notNull(),
    resendId: varchar('resend_id', { length: 128 }),
    linterReport: jsonb('linter_report'),
    // Additive (W1-1 Gmail wire-up, 2026-07-02): the draft's intent, per the
    // brief's request shape {jobId, kind}. Default 'application' so pre-existing
    // rows (none live yet — this route is new) and any writer that omits it never
    // breaks. 'follow_up' is reserved for a future bump-with-new-info flow; this
    // slice only ever writes 'application' (no follow-up composer exists yet).
    kind: varchar('kind', { length: 24 }).default('application').notNull(),
    // Additive: the real v1 send sink is Gmail Drafts (GmailService.createDraft),
    // NOT Resend — resendId above is a stale relic from the deferred v1.5 path,
    // left untouched (no deletion). These carry the Gmail draft identifiers so a
    // GET list can deep-link straight to the user's own Drafts folder.
    gmailDraftId: varchar('gmail_draft_id', { length: 128 }),
    gmailWebUrl: text('gmail_web_url'),
    // Additive (W1-15, R164: public launch = Resend-draft delivery). Which
    // delivery path actually carried this draft: 'gmail' (the user's own
    // Gmail Drafts folder, unchanged W1-1 path) | 'resend' (emailed to the
    // user's OWN inbox via ResendService, the new public path — never a
    // third party) | 'none' (reserved for a possible future path where a
    // composed-but-undelivered draft is persisted anyway; the CURRENT
    // deliverDraft never writes this value — when neither Gmail nor Resend is
    // available it refuses with a 409 and writes NO row at all, so nothing is
    // ever silently lost or half-recorded). Nullable: every pre-existing row
    // predates this column entirely (they were all delivered via the
    // pre-W1-15 Gmail-only path, before 'gmail'/'resend'/'none' existed as a
    // concept) — NULL is the only honest backfill (NO INVENTION forbids
    // guessing which of the three labels a historical row "would have"
    // gotten). Every NEW row written by deliverDraft sets this explicitly to
    // 'gmail' or 'resend' (the only two values current code ever persists).
    deliveredVia: varchar('delivered_via', { length: 16 }),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('outreach_user_idx').on(t.userId),
    statusIdx: index('outreach_status_idx').on(t.status),
  }),
);

export const references = pgTable(
  'references',
  {
    id: id(),
    userId: text('user_id').notNull(),
    fullName: varchar('full_name', { length: 200 }).notNull(),
    relationship: varchar('relationship', { length: 200 }),
    company: varchar('company', { length: 200 }),
    email: varchar('email', { length: 320 }),
    phone: varchar('phone', { length: 64 }),
    contexts: jsonb('contexts').$type<string[]>(),
    lastAskedAt: date('last_asked_at'),
    notes: text('notes'),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('references_user_idx').on(t.userId),
  }),
);
